import React, { useEffect, useState } from "react";

/**
 * Bottom-sheet style context menu shown after long-pressing a conversation.
 * Mirrors WhatsApp/iMessage native UX.
 *
 * Actions:
 *   - Toggle favorite (star)
 *   - Toggle resolved (checkmark)
 *   - Rename (prompt)
 *   - Open labels modal
 *   - Delete (confirm)
 */
export default function ConversationContextMenu({
  conv,
  onClose,
  onPatch,        // (convId, patch) => Promise<void>
  onRename,      // (convId, newTitle) => Promise<void>
  onDelete,       // (convId) => Promise<void>
  onOpenLabels,  // (conv) => void
}) {
  const [closing, setClosing] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const close = () => {
    setClosing(true);
    setTimeout(onClose, 180);
  };

  if (!conv) return null;
  const isFav = !!conv.favorite;
  const isResolved = conv.status === "resolved";

  return (
    <div
      data-testid="conv-context-menu"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        background: closing ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        zIndex: 60,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        transition: "background 180ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "var(--pl-card)",
          border: "1px solid rgba(0, 200, 255, 0.22)",
          borderRadius: "20px 20px 0 0",
          padding: "8px 0 calc(8px + env(safe-area-inset-bottom, 0px))",
          boxShadow: "0 -12px 40px rgba(0,0,0,0.45)",
          transform: closing ? "translateY(100%)" : "translateY(0)",
          transition: "transform 180ms ease",
        }}
      >
        {/* Grabber handle */}
        <div
          style={{
            width: 40,
            height: 4,
            background: "rgba(255,255,255,0.18)",
            borderRadius: 999,
            margin: "8px auto 12px",
          }}
        />
        {/* Conversation title */}
        <div
          style={{
            padding: "0 20px 12px",
            borderBottom: "1px solid var(--pl-border)",
            marginBottom: 4,
          }}
        >
          <div style={{ fontSize: 13, color: "var(--pl-text-muted)", marginBottom: 2 }}>
            Consulta
          </div>
          <div
            style={{
              fontSize: 16,
              color: "var(--pl-text-strong)",
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {conv.title || "Sin título"}
          </div>
        </div>

        <MenuItem
          testId="ctx-favorite"
          icon={
            <svg viewBox="0 0 24 24" width="20" height="20" fill={isFav ? "var(--pl-primary)" : "none"} stroke="var(--pl-primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          }
          label={isFav ? "Quitar de favoritas" : "Añadir a favoritas"}
          onClick={async () => {
            await onPatch(conv.conv_id, { favorite: !isFav });
            close();
          }}
        />

        <MenuItem
          testId="ctx-resolve"
          icon={
            isResolved ? (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="var(--pl-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )
          }
          label={isResolved ? "Marcar como abierta" : "Marcar como resuelta"}
          onClick={async () => {
            await onPatch(conv.conv_id, { status: isResolved ? "open" : "resolved" });
            close();
          }}
        />

        <MenuItem
          testId="ctx-rename"
          icon={
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="var(--pl-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          }
          label="Renombrar"
          onClick={() => {
            const next = window.prompt("Nuevo nombre de la consulta:", conv.title || "");
            if (next && next.trim() && next.trim() !== conv.title) {
              onRename(conv.conv_id, next.trim());
            }
            close();
          }}
        />

        <MenuItem
          testId="ctx-label"
          icon={
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="var(--pl-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
          }
          label={conv.tag?.name ? `Etiqueta: ${conv.tag.name}` : "Añadir etiqueta"}
          color={conv.tag?.color}
          onClick={() => {
            onOpenLabels && onOpenLabels(conv);
            close();
          }}
        />

        <MenuItem
          testId="ctx-delete"
          icon={
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
          }
          label="Eliminar"
          danger
          onClick={() => {
            if (window.confirm(`¿Eliminar "${conv.title || 'esta consulta'}"?`)) {
              onDelete(conv.conv_id);
            }
            close();
          }}
        />

        <button
          type="button"
          onClick={close}
          data-testid="ctx-cancel"
          style={{
            display: "block",
            width: "calc(100% - 32px)",
            margin: "10px 16px 6px",
            padding: "12px 16px",
            borderRadius: 14,
            border: "1px solid var(--pl-border)",
            background: "transparent",
            color: "var(--pl-text)",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function MenuItem({ icon, label, onClick, danger, color, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        padding: "14px 20px",
        background: "transparent",
        border: "none",
        textAlign: "left",
        color: danger ? "#ef4444" : "var(--pl-text)",
        fontSize: 15,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      <span style={{ width: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </span>
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      {color && (
        <span
          aria-hidden
          style={{ width: 12, height: 12, borderRadius: 999, background: color, flexShrink: 0 }}
        />
      )}
    </button>
  );
}
